import {
  ArrowRight,
  CalendarClock,
  CalendarDays,
  MapPin,
  Ruler,
  type LucideIcon
} from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { OfferDetail, OfferList } from 'types/api'

import { SNA_IMAGE_PREFIX } from '@services/utils'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useIntlLocale } from '@/i18n/languages'

// Solid tones — legible as an overlay on top of photos.
const STATUS_TONE: Record<string, string> = {
  CREATED: 'bg-primary text-white',
  INTERESTED: 'bg-peach text-white',
  ACCEPTED: 'bg-primary text-white',
  RESERVED: 'bg-primary text-white',
  ONGOING: 'bg-primary text-white',
  REJECTED: 'bg-destructive text-white',
  TERMINATED: 'bg-destructive text-white',
  NOTICE_PERIOD: 'bg-peach text-white'
}

function Meta({
  icon: Icon,
  label,
  value
}: {
  icon: LucideIcon
  label: string
  value: ReactNode
}) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-ink">{value}</span>
    </div>
  )
}

export function OfferItem({ offer }: { offer: OfferDetail | OfferList }) {
  const { t } = useTranslation('offers')
  const flat = offer.flat
  const detailUrl = `/user/offer/${offer?.id}`
  const locale = useIntlLocale()

  const tone =
    (offer.status && STATUS_TONE[offer.status]) || 'bg-ink text-white'

  return (
    <Card className="group overflow-hidden p-0 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(4,33,37,0.10)]">
      <div className="flex flex-col sm:flex-row">
        <Link
          to={detailUrl}
          className="relative block shrink-0 overflow-hidden sm:w-[42%] sm:max-w-[340px]"
        >
          <div className="aspect-[4/3] w-full bg-ink/[0.04] sm:h-full">
            <img
              src={`${SNA_IMAGE_PREFIX}${flat?.main_image}`}
              alt={flat?.title ?? 'Flat'}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              onError={(e) => {
                e.currentTarget.style.visibility = 'hidden'
              }}
            />
          </div>
          {offer.status && (
            <span
              className={cn(
                'absolute left-3 top-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shadow-sm',
                tone
              )}
            >
              {t(`status.${offer.status.toLowerCase()}`)}
            </span>
          )}
        </Link>

        <div className="flex flex-1 flex-col gap-4 p-5 sm:p-6">
          <Link to={detailUrl} className="group/title block">
            <h3 className="m-0 font-display text-xl font-bold leading-tight tracking-tight text-ink transition-colors group-hover/title:text-primary">
              {flat?.title}
            </h3>
          </Link>

          <div className="flex flex-col gap-2">
            {flat?.location_name && (
              <Meta
                icon={MapPin}
                label={t('location')}
                value={flat.location_name}
              />
            )}
            {flat?.area != null && (
              <Meta
                icon={Ruler}
                label={t('size')}
                value={
                  <>
                    {flat.area} m<sup>2</sup>
                  </>
                }
              />
            )}
            {flat?.availability_date && (
              <Meta
                icon={CalendarDays}
                label={t('availableFrom')}
                value={new Date(flat.availability_date).toLocaleDateString(
                  locale,
                  { day: 'numeric', month: 'short', year: 'numeric' }
                )}
              />
            )}
            {offer.deadline &&
              offer.status &&
              ['CREATED', 'INTERESTED', 'ACCEPTED'].includes(offer.status) && (
                <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-peach/30 px-3 py-1 text-xs font-semibold text-warning">
                  <CalendarClock className="h-3.5 w-3.5" />
                  {t('sections.deadline')}:{' '}
                  {new Date(offer.deadline).toLocaleDateString(locale)}
                </span>
              )}
          </div>

          <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-1">
            <div className="flex items-baseline gap-1.5">
              <span className="font-display text-2xl font-bold text-ink">
                {offer?.monthly_rent_sum} €
              </span>
              <span className="text-sm text-muted-foreground">
                {t('perMonth')}
              </span>
            </div>
            <Button asChild>
              <Link to={detailUrl}>
                {t('moreInfo')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}
